import React from 'react';
import { forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import useIntersectionObserver from '../../../hooks/useIntersectionObserver';
import THEME from '../../../constants/theme';

// Helper function to render text with embedded React Router Links
const renderTextWithReactLinks = (textContent, linkMappings = []) => {
  if (!textContent) return '';

  let processedTextContent = textContent.replace(/\\n/g, '\n'); // Handle explicit newlines

  // Enhanced to handle ##Program Name## syntax
  const programNameRegex = /##(.*?)##/g;
  let segments = [];
  let lastIndex = 0;
  let match;

  while ((match = programNameRegex.exec(processedTextContent)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push(processedTextContent.substring(lastIndex, match.index));
    }
    // Add the highlighted program name as a special object or styled span
    segments.push(
      <Typography
        component='span'
        key={`program-${match[1]}-${match.index}`}
        sx={{
          fontSize: '1.1em', // Slightly larger font size
          fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD // Bold
        }}
      >
        {match[1]}
      </Typography>
    );
    lastIndex = programNameRegex.lastIndex;
  }
  // Add any remaining text after the last match
  if (lastIndex < processedTextContent.length) {
    segments.push(processedTextContent.substring(lastIndex));
  }

  // Further process segments for links
  let finalProcessedSegments = [];
  segments.forEach((segment, segIndex) => {
    if (typeof segment === 'string') {
      let currentText = segment;
      const internalLinkMappings = linkMappings.filter(
        lm => lm.url && !lm.newTab
      );
      const anchorLinkMappings = linkMappings.filter(
        lm => !lm.url || lm.newTab
      );
      let linkProcessedParts = [currentText];

      internalLinkMappings.forEach(({ word, url, color, textDecoration }) => {
        let newLinkParts = [];
        linkProcessedParts.forEach(part => {
          if (typeof part === 'string') {
            const regex = new RegExp(`\\b${word}\\b`);
            const linkMatch = part.match(regex);
            if (linkMatch) {
              if (part.substring(0, linkMatch.index)) {
                newLinkParts.push(part.substring(0, linkMatch.index));
              }
              newLinkParts.push(
                <RouterLink
                  key={`${url}-${word}-${linkMatch.index}-${segIndex}`}
                  to={url}
                  style={{
                    color: color || THEME.COLORS.PRIMARY_BLUE,
                    textDecoration:
                      textDecoration === 'underlined'
                        ? 'underline'
                        : textDecoration || 'underline'
                  }}
                >
                  {word}
                </RouterLink>
              );
              if (part.substring(linkMatch.index + word.length)) {
                newLinkParts.push(
                  part.substring(linkMatch.index + word.length)
                );
              }
            } else {
              newLinkParts.push(part);
            }
          } else {
            newLinkParts.push(part);
          }
        });
        linkProcessedParts = newLinkParts;
      });

      let finalAnchorParts = [];
      linkProcessedParts.forEach(part => {
        if (typeof part === 'string') {
          let htmlString = part;
          anchorLinkMappings.forEach(
            ({ word, url, color, textDecoration, newTab }) => {
              const regex = new RegExp(`\\b${word}\\b`, 'g');
              htmlString = htmlString.replace(
                regex,
                m =>
                  `<a href="${url}" ${newTab ? 'target="_blank" rel="noopener noreferrer"' : ''} style="color: ${color || THEME.COLORS.PRIMARY_BLUE}; text-decoration: ${textDecoration === 'underlined' ? 'underline' : textDecoration || 'underline'};">${m}</a>`
              );
            }
          );
          if (htmlString !== part) {
            finalAnchorParts.push(
              <span
                key={`html-${finalAnchorParts.length}-${segIndex}`}
                dangerouslySetInnerHTML={{ __html: htmlString }}
              />
            );
          } else {
            finalAnchorParts.push(part);
          }
        } else {
          finalAnchorParts.push(part);
        }
      });
      finalProcessedSegments.push(...finalAnchorParts);
    } else {
      // This segment is already a React component (our styled program name)
      finalProcessedSegments.push(segment);
    }
  });

  return finalProcessedSegments.map((part, index) => (
    <React.Fragment key={`frag-${index}`}>{part}</React.Fragment>
  ));
};

const SectionContent = forwardRef(
  (
    {
      id,
      title,
      titleVariant = 'TitleMedium',
      titleMargin = '0 0 1rem 0',
      titleColor,
      initialSpacing = true,
      text = [],
      linkMappings = [],
      titleSx = {},
      textSx = {},
      titleTag = 'h2',
      children
    },
    ref
  ) => {
    const contentRef = React.useRef(null);
    const isVisible = useIntersectionObserver(contentRef);

    const getTitleStyles = () => {
      switch (titleVariant) {
        case 'TitleLarge':
          return {
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD
          };
        case 'TitleMedium':
          return {
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
          };
        case 'TitleSmall':
          return {
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
          };
        case 'TitleSmallBlue':
          return {
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
            color: THEME.COLORS.PRIMARY_BLUE
          };
        default:
          return {
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
          };
      }
    };

    const defaultBodyFontSize = '1.3rem';

    return (
      <Box
        id={id}
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          marginTop: initialSpacing ? THEME.SPACING.LARGE : '0',
          marginBottom: text.length > 0 || children ? '0' : THEME.SPACING.LARGE
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: `opacity ${THEME.TRANSITIONS.SLOW} ease, transform ${THEME.TRANSITIONS.SLOW} ease`
          }}
        >
          {title && (
            <Typography
              variant='body1'
              component={titleTag}
              sx={{
                ...getTitleStyles(),
                margin: titleMargin,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                textAlign: 'left',
                color: titleColor || 'inherit',
                ...titleSx
              }}
            >
              {title}
            </Typography>
          )}{' '}
          {text.map(
            (
              {
                text: textContent,
                marginBottom = THEME.SPACING.MEDIUM,
                marginTop = '0',
                marginLeft = '0',
                padding = '0',
                backgroundColor = 'transparent',
                color = 'inherit',
                hoverColor,
                fontStyle = 'normal',
                textAlign = 'left',
                fontSize = defaultBodyFontSize,
                textShadow,
                variant = 'body1',
                component = 'div'
              },
              index
            ) => (
              <Box
                key={index}
                sx={{
                  marginBottom:
                    component === 'h2' || component === 'p' ? 0 : marginBottom,
                  marginTop:
                    component === 'h2' || component === 'p' ? 0 : marginTop,
                  marginLeft,
                  padding,
                  backgroundColor,
                  color,
                  display:
                    component === 'h2' || component === 'p'
                      ? 'inline'
                      : undefined,
                  transition: `background-color ${THEME.TRANSITIONS.MEDIUM} ease`,
                  fontStyle,
                  textAlign,
                  '&:hover': {
                    backgroundColor: hoverColor
                  }
                }}
              >
                <Typography
                  variant={variant}
                  component={component}
                  sx={{
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    fontSize: fontSize,
                    lineHeight: 1.6,
                    textShadow: textShadow,
                    whiteSpace: 'pre-line',
                    display:
                      component === 'h2' || component === 'p'
                        ? 'inline'
                        : undefined,
                    margin: 0,
                    ...textSx
                  }}
                >
                  {renderTextWithReactLinks(textContent, linkMappings)}
                </Typography>
              </Box>
            )
          )}
          {children}
        </Box>
      </Box>
    );
  }
);

export default SectionContent;
